import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '#AdminPanelFrontend/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '#AdminPanelFrontend/components/ui/collapsible';
import { useMenu } from '#AdminPanelFrontend/contexts/MenuContext';

interface SidebarItem {
	title: string;
	icon: React.ElementType;
	path?: string;
	subItems?: SidebarItem[];
}

interface SidebarProps {
	isOpen: boolean;
}

const SidebarLink: React.FC<{ item: SidebarItem; isNested?: boolean }> = ({ item, isNested = false }) => {
	const [isOpen, setIsOpen] = useState(false);

	if (item.subItems) {
		return (
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<CollapsibleTrigger asChild>
					<button
						className={cn(
							'flex items-center w-full p-2 text-gray-300 transition-colors rounded-md hover:bg-gray-700 hover:text-white',
							isNested && 'pl-6'
						)}
					>
						<item.icon />
						<span className="ml-3 flex-1 text-left">{item.title}</span>
						{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
					</button>
				</CollapsibleTrigger>
				<CollapsibleContent>
					{item.subItems.map((subItem) => (
						<SidebarLink key={subItem.title} item={subItem} isNested />
					))}
				</CollapsibleContent>
			</Collapsible>
		);
	}

	return (
		<Link
			to={item.path || '#'}
			className={cn(
				'flex items-center p-2 text-gray-300 transition-colors rounded-md hover:bg-gray-700 hover:text-white',
				isNested && 'pl-6'
			)}
		>
			<item.icon />
			<span className="ml-3">{item.title}</span>
		</Link>
	);
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
	const { menuItems, loading } = useMenu();

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<aside
			className={cn(
				'bg-gray-800 text-white w-64 min-h-screen p-4 transition-all duration-300 ease-in-out',
				isOpen ? 'translate-x-0' : '-translate-x-full',
				'lg:w-72 fixed inset-y-0 left-0 z-40'
			)}
		>
			<nav className={cn('space-y-2', isOpen ? 'translate-x-0' : '-translate-x-full')}>
				{menuItems.map((item) => (
					<SidebarLink key={item.title} item={item} />
				))}
			</nav>
		</aside>
	);
};

export default Sidebar;
